<template>
  <div class="space-y-5">
    <section
      class="acc-profile-hero-surface acc-profile-hero-identity-viewport acc-glass relative isolate overflow-hidden rounded-2xl border border-white/10 p-4 sm:p-5"
      :style="heroCoverCssVars"
    >
      <div class="acc-profile-hero-fallback absolute inset-0" aria-hidden="true" />
      <img v-if="heroCover" :src="heroCover" class="acc-profile-hero-cover absolute inset-0 h-full w-full" alt="" aria-hidden="true" />
      <div class="acc-profile-hero-scrim absolute inset-0" aria-hidden="true" />
      <div class="relative z-10 grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div class="acc-profile-hero-identity-shell acc-profile-hero-identity-shell--responsive lg:col-span-8">
          <user-avatar
            :src="profile && profile.avatar ? profile.avatar : ''"
            :name="profile && profile.name ? profile.name : ''"
            alt="Аватар користувача"
            size="hero"
            loading="eager"
            class="acc-profile-hero-avatar bg-foxy-surface"
          />
          <div class="acc-profile-hero-identity-copy">
            <div v-if="profile && profile.name" class="text-xl sm:text-2xl font-extrabold break-words sm:truncate">{{ profile.name }}
            </div>
            <div v-else class="text-xl sm:text-2xl font-extrabold">Користувач</div>
            <div class="text-sm text-white/60">@{{ profile && profile.username ? profile.username : 'guest' }}</div>
            <div v-if="profile && profile.bio" class="mt-2 text-xs text-white/70 line-clamp-2">
              {{ profile.bio }}
            </div>
            <div class="mt-1 flex flex-wrap justify-center sm:justify-start items-center gap-x-5 gap-y-1 text-xs text-white/65">
              <span>{{ formatCount(stats.reviews_count || 0, 'рецензія', 'рецензії', 'рецензій') }}</span>
              <span>{{ formatCount(stats.discussions_count || 0, 'обговорення', 'обговорення', 'обговорень') }}</span>
            </div>
          </div>
        </div>

        <div class="lg:col-span-4">
          <div class="acc-glass rounded-2xl p-3 border border-white/10">
            <div class="flex gap-2 mb-3 text-xs">
              <button class="acc-btn !px-3 !py-1" :class="{ 'opacity-70': overviewStatsTab !== 'books' }"
                @click="overviewStatsTab = 'books'">Книги</button>
              <button class="acc-btn !px-3 !py-1" :class="{ 'opacity-70': overviewStatsTab !== 'reviews' }"
                @click="overviewStatsTab = 'reviews'">Рецензії</button>
              <button class="acc-btn !px-3 !py-1" :class="{ 'opacity-70': overviewStatsTab !== 'discussions' }"
                @click="overviewStatsTab = 'discussions'">Обговорення</button>
              <!-- <button class="acc-btn !px-3 !py-1" :class="{ 'opacity-70': overviewStatsTab !== 'quotes' }" @click="overviewStatsTab = 'quotes'">Цитати</button> -->
            </div>
            <div class="grid grid-cols-[86px,1fr] items-center gap-3">
              <div class="relative h-[86px] w-[86px] rounded-full">
                <svg class="h-[86px] w-[86px] -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
                  <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" class="text-white/15"
                    stroke-width="8" />
                  <circle cx="50" cy="50" r="44" fill="none" :stroke="overviewRingColor" stroke-width="8"
                    stroke-linecap="round" :stroke-dasharray="overviewRingDasharray" stroke-dashoffset="0" />
                </svg>
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="text-2xl font-extrabold" :style="{ color: overviewRingColor }">{{ overviewMainValue }}
                  </div>
                </div>
              </div>
              <ul class="space-y-1 text-xs text-white/70">
                <li v-for="row in overviewRows" :key="row.key" class="flex justify-between">
                  <span>{{ row.label }}</span><span>{{ row.value }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-4 lg:grid-cols-12">
      <div class="lg:col-span-8 space-y-4">
        <div class="text-base font-extrabold">Статистика</div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="acc-glass rounded-2xl p-4 border border-white/10">
            <div class="text-xs text-white/60 mb-3">Активність</div>
            <div class="h-14 rounded-xl bg-white/5 border border-white/10 flex items-end px-3 pb-2 gap-2">
              <span v-for="(bar, idx) in activityBars" :key="`bar-${idx}`"
                class="w-1 rounded-full bg-foxy-action"
                :style="{ height: `${bar.height}px` }" :title="`${bar.label}: ${bar.value}`" />
            </div>
            <div class="mt-3 grid grid-cols-2 gap-2 text-[11px] text-white/70">
              <div class="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
                За 14 днів: <span class="font-bold text-white/90">{{ activitySummary.total }}</span>
              </div>
              <div class="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
                Сьогодні: <span class="font-bold text-white/90">{{ activitySummary.today }}</span>
              </div>
              <div class="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
                Середнє/день: <span class="font-bold text-white/90">{{ activitySummary.avg }}</span>
              </div>
              <div class="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
                Пік: <span class="font-bold text-white/90">{{ activitySummary.peakLabel }}</span>
              </div>
            </div>
            <div class="mt-2 text-[11px] text-white/55">
              Враховуються дії: оновлення статусів книг, рецензії, обговорення, цитати.
            </div>
          </div>
          <div class="acc-glass rounded-2xl p-4 border border-white/10">
            <div class="text-xs text-white/60 mb-3">Прогрес читання</div>
            <div class="text-2xl font-extrabold">{{ formatCount(stats.read_count || 0, 'книга', 'книги', 'книг') }}</div>
            <div class="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
              <div class="h-full bg-foxy-action"
                :style="{ width: `${progressWidth}%` }" />
            </div>
            <div class="mt-1 text-xs text-white/55 text-right">план: {{ stats.planned_count || 0 }} | цілі: {{
              stats.planner_done_items || 0 }}/{{ stats.planner_total_items || 0 }}</div>
          </div>
        </div>

        <div class="acc-glass rounded-2xl p-4 border border-white/10">
          <div class="text-xs text-white/60 mb-2">Розширена статистика</div>
          <div class="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div class="rounded-lg border border-white/10 bg-white/5 p-2">
              <div class="text-white/55">Усього книг</div>
              <div class="font-bold text-base">{{ stats.total_books_count || 0 }}</div>
            </div>
            <div class="rounded-lg border border-white/10 bg-white/5 p-2">
              <div class="text-white/55">Оцінено</div>
              <div class="font-bold text-base">{{ stats.rated_count || 0 }}</div>
            </div>
            <div class="rounded-lg border border-white/10 bg-white/5 p-2">
              <div class="text-white/55">Середня оцінка</div>
              <div class="font-bold text-base">{{ stats.average_rating || '—' }}</div>
            </div>
            <div class="rounded-lg border border-white/10 bg-white/5 p-2">
              <div class="text-white/55">Закинуто</div>
              <div class="font-bold text-base">{{ stats.dropped_count || 0 }}</div>
            </div>
          </div>
        </div>

                <section v-if="showFavoriteWorks" aria-labelledby="overview-favorite-books-title" data-overview-favorite-books>
                    <div class="mb-3 flex flex-col items-start justify-between gap-2 md:flex-row md:items-center">
                        <div class="flex flex-wrap items-baseline gap-2">
                            <h2 id="overview-favorite-books-title" class="text-base font-extrabold">Улюблені книги</h2>
                            <span v-if="favoriteWorkCount" class="text-xs text-foxy-text-secondary">{{ formatCount(favoriteWorkCount, 'книга', 'книги', 'книг') }}</span>
                        </div>
                        <a v-if="favoriteWorkCount && favoriteWorksUrl" :href="favoriteWorksUrl" class="inline-flex text-xs font-semibold text-foxy-action hover:text-foxy-action/80">Переглянути всі →</a>
                    </div>
                    <div class="acc-glass rounded-2xl border border-white/10 p-4">
                        <div v-if="favoriteWorkItems.length" class="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
                            <article v-for="item in favoriteWorkItems" :key="item.work_id" class="book-shelf-item block min-w-0 rounded-xl" data-overview-favorite-book>
                                <a :href="item.url" :aria-label="'Відкрити книгу «' + item.title + '»'"
                                    class="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-bg"
                                    @mouseenter="openFavoriteWorkPreview(item, $event)" @mouseleave="closeFavoriteWorkPreview"
                                    @focus="openFavoriteWorkPreview(item, $event, true)" @blur="closeFavoriteWorkPreview">
                                    <div class="book-cover-frame relative aspect-[2/3] rounded-xl shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                                        <img :src="resolveBookCover(item.cover)" class="book-cover-media" alt="" loading="lazy" decoding="async" width="200" height="300" @error="onCoverError" />
                                        <book-status-bookmark v-if="favoriteWorkViewerStatus(item)" :status="favoriteWorkViewerStatus(item)" />
                                    </div>
                                </a>
                                <div class="mt-2.5 px-0.5">
                                    <a :href="item.url" class="group block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action" :title="item.title">
                                        <h3 class="text-sm font-semibold text-foxy-text leading-snug line-clamp-2 group-hover:text-foxy-action transition-colors">{{ item.title }}</h3>
                                    </a>
                                    <p class="mt-1 text-xs text-foxy-text-secondary line-clamp-1">
                                        <template v-if="item.authors && item.authors.length">
                                            <span v-for="(author, index) in item.authors" :key="index">
                                                <span v-if="index">, </span><a v-if="author.url" :href="author.url"
                                                    class="book-author-link rounded-sm transition-colors hover:text-foxy-action focus-visible:text-foxy-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action">{{ author.name }}</a><span v-else>{{ author.name }}</span>
                                            </span>
                                        </template>
                                        <template v-else>{{ item.author && item.author.name }}</template>
                                    </p>
                                </div>
                                <button v-if="favoriteWorkPreviewAvailable" type="button" class="favorite-work-preview-trigger acc-btn hidden text-xs md:inline-flex"
                                    :aria-label="'Швидкий перегляд книги «' + item.title + '»'"
                                    :aria-controls="favoriteWorkPreviewId" :aria-expanded="favoriteWorkPreviewOpenId === item.work_id ? 'true' : 'false'"
                                    :disabled="!favoriteWorkPreviewItem(item)" @focus="openFavoriteWorkPreview(item, $event, true)" @blur="closeFavoriteWorkPreview"
                                    @click="openFavoriteWorkPreview(item, $event, true)" @keydown="favoriteWorkPreviewKeydown(item, $event)">Швидкий перегляд</button>
                            </article>
                        </div>
                        <template v-else>
                            <div class="text-sm font-bold">Ще немає улюблених книг.</div>
                            <div class="mt-1 text-xs text-foxy-text-secondary">Додати книгу до улюбленого можна на її сторінці.</div>
                        </template>
                    </div>
                    <book-quick-preview-host v-if="favoriteWorkPreviewAvailable" ref="favoriteWorkQuickPreview" :items="favoriteWorkPreviewItems"
                        :refresh-items="refreshFavoriteWorkViewerContexts" surface-context="PUBLIC_FOREIGN_LIBRARY" :authenticated="favoriteWorkPreviewAvailable"
                        @manage="openFavoriteWorkViewerManagement" @opened="favoriteWorkPreviewOpenId = $event" @closed="favoriteWorkPreviewOpenId = null" />
                </section>

        <div>
          <div class="flex items-start md:items-center flex-col md:flex-row justify-between mb-3 gap-2">
            <div class="text-base font-extrabold">Збережене</div>
            <div class="flex gap-1 text-xs">
              <button class="acc-btn !px-2 !py-1" :class="{ 'opacity-70': activeFavoritesTab !== 'reviews' }"
                @click="activeFavoritesTab = 'reviews'">Рецензії</button>
              <button class="acc-btn !px-2 !py-1" :class="{ 'opacity-70': activeFavoritesTab !== 'quotes' }"
                @click="activeFavoritesTab = 'quotes'">Цитати</button>
            </div>
          </div>
          <div class="acc-glass rounded-2xl p-5 border border-white/10">
            <div v-if="favoriteItems.length" class="space-y-3">
              <div v-for="item in favoriteItems.slice(0, 3)" :key="`${activeFavoritesTab}-${item.id}`"
                class="flex items-center gap-3">
                <img :src="resolveBookCover(item.cover)" class="h-11 w-8 rounded object-cover border border-white/10"
                  alt="" @error="onCoverError" />
                <div class="min-w-0">
                  <div class="text-sm font-semibold truncate">{{ item.title }}</div>
                  <div class="text-xs text-white/55">{{ item.meta }}</div>
                </div>
              </div>
              <a v-if="profile && profile.username" :href="`/account/u/${profile.username}/saved`"
                class="inline-flex text-xs font-semibold text-foxy-action hover:text-foxy-action/80">
                Переглянути все →
              </a>
            </div>
            <template v-else>
              <div class="text-sm font-bold">Поки порожньо</div>
              <div class="text-xs text-white/60 mt-1">
                Додайте рецензію або цитату в збережене окремою дією (★) — книги з бібліотеки сюди не потрапляють автоматично.
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="lg:col-span-4 space-y-4">
        <div>
          <div class="text-base font-extrabold mb-3">Історія</div>
          <div class="acc-glass rounded-2xl p-5 border border-white/10">
            <div class="space-y-2 text-sm">
              <div class="flex justify-between"><span class="text-white/70">Рецензії</span><span class="font-bold">{{
                stats.reviews_count || 0 }}</span></div>
              <div class="flex justify-between"><span class="text-white/70">Обговорення</span><span class="font-bold">{{
                stats.discussions_count || 0 }}</span></div>
              <div class="flex justify-between"><span class="text-white/70">Цитати</span><span class="font-bold">{{
                stats.quotes_count || 0 }}</span></div>
              <div class="flex justify-between"><span class="text-white/70">Добірки</span><span class="font-bold">{{
                stats.collections_count || 0 }}</span></div>
            </div>
          </div>
        </div>
        <div>
          <div class="text-base font-extrabold mb-3">Нагороди</div>
          <div class="acc-glass rounded-2xl p-5 border border-white/10">
            <div v-if="awards.length" class="grid grid-cols-2 gap-2">
              <button v-for="award in awards.slice(0, 8)" :key="`award-${award.id}`" type="button"
                class="rounded-xl border border-white/10 bg-white/5 p-2 text-left transition hover:bg-white/10"
                @click="openAwardModal(award)">
                <div class="flex items-center gap-2">
                  <div class="flex items-center">
                    <img v-if="award.image" :src="award.image" :alt="award.name"
                      class="h-8 w-8 rounded-full object-cover border border-white/10" />
                    <div v-else
                      class="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white/95"
                      :style="{ backgroundColor: award.color || 'rgb(var(--foxy-brand))' }">
                      {{ (award.name || '?').slice(0, 1).toUpperCase() }}
                    </div>
                  </div>
                  <div class="min-w-0">
                    <div class="text-xs font-bold truncate">{{ award.name }}</div>
                    <div v-if="award.points" class="text-[11px] text-white/60">+{{ formatCount(award.points, 'бал', 'бали', 'балів') }}</div>
                  </div>
                </div>
              </button>
            </div>
            <template v-else>
              <div class="text-sm font-bold">Нагород поки немає</div>
              <div class="text-xs text-white/60 mt-1">Після активності в профілі тут з’являться ваші відзнаки.</div>
            </template>
          </div>
        </div>
        <div>
          <div class="flex items-center justify-between mb-3">
            <div class="text-base font-extrabold">Добірки</div>
            <a class="acc-btn !px-3 !py-1" :href="librariesHubUrl">→</a>
          </div>
          <a :href="librariesHubUrl" class="acc-glass block rounded-2xl p-5 border border-white/10 transition hover:border-white/20">
            <div v-if="collections.length" class="space-y-2">
              <div v-for="col in collections.slice(0, 3)" :key="col.id"
                class="flex items-center justify-between text-sm">
                <div class="flex items-center gap-2 min-w-0">
                  <div class="grid grid-cols-3 gap-0.5 h-8 w-14 shrink-0">
                    <template v-if="col.preview_covers && col.preview_covers.length">
                      <img v-for="(cover, idx) in col.preview_covers.slice(0, 3)" :key="`cover-${col.id}-${idx}`"
                        :src="resolveBookCover(cover)" class="h-8 w-full rounded-sm object-cover border border-white/10" alt="" @error="onCoverError">
                    </template>
                    <template v-else>
                      <div v-for="idx in 3" :key="`empty-${col.id}-${idx}`"
                        class="h-8 rounded-sm bg-white/10 border border-white/10" />
                    </template>
                  </div>
                  <span class="truncate">{{ col.name }}</span>
                </div>
                <span class="text-white/55">{{ formatBookCount(col.books_count) }}</span>
              </div>
            </div>
            <template v-else>
              <div class="text-sm font-bold">Добірки відсутні</div>
              <div class="text-xs text-white/60 mt-1">Створіть свою першу добірку.</div>
            </template>
          </a>
        </div>
        <div v-if="isOwner">
          <div class="flex items-center justify-between mb-3">
            <div class="text-base font-extrabold">План читання</div>
            <button class="acc-btn-primary !px-3 !py-1" @click="openPlannerModal">+ Новий план</button>
          </div>
          <div class="acc-glass rounded-2xl p-5 border border-white/10">
            <div v-if="readingPlans.length" class="space-y-3">
              <div v-for="plan in readingPlans" :key="`plan-${plan.id}`"
                class="rounded-xl border border-white/10 bg-white/5 p-3">
                <div class="flex items-center justify-between gap-2">
                  <div class="font-bold text-sm">{{ plan.title }}</div>
                  <div class="text-xs text-white/60">{{ plan.done_items }}/{{ plan.total_items }}</div>
                </div>
                <div class="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div class="h-full bg-gradient-to-r from-emerald-400 to-teal-500"
                    :style="{ width: `${plan.progress || 0}%` }" />
                </div>
                <div class="mt-2 space-y-1">
                  <label v-for="item in plan.items.slice(0, 4)" :key="`plan-item-${item.id}`"
                    class="flex items-center gap-2 text-xs">
                    <input type="checkbox" :checked="item.is_done" @change="togglePlanItem(plan, item)">
                    <span class="truncate">{{ item.book ? item.book.title : 'Книга' }}</span>
                  </label>
                </div>
              </div>
            </div>
            <div v-else class="text-sm text-white/55">Ще немає планів читання.</div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="showPlannerModal" class="fixed inset-0 z-[140]">
      <div class="acc-modal-overlay" @click="showPlannerModal = false" />
      <div class="absolute inset-0 flex items-center justify-center p-4">
        <div class="acc-modal max-w-2xl">
          <div class="text-base font-extrabold">Новий план читання</div>
          <div class="mt-3 space-y-3">
            <input v-model.trim="plannerForm.title" type="text" class="acc-modal-input" placeholder="Назва плану">
            <textarea v-model.trim="plannerForm.goal" rows="3" class="acc-modal-input" placeholder="Мета"></textarea>
            <input v-model="plannerForm.target_date" type="date" class="acc-modal-input">
            <div class="acc-modal-subpanel">
              <div class="flex gap-2">
                <input v-model.trim="plannerBookQuery" type="text" class="acc-modal-input"
                  placeholder="Знайти книги для плану">
                <button class="acc-btn" @click="searchPlannerBooks">Пошук</button>
              </div>
              <div v-if="plannerSearchResults.length" class="mt-2 max-h-48 overflow-auto space-y-1">
                <label v-for="book in plannerSearchResults" :key="`plan-search-${book.id}`"
                  class="flex items-center gap-2 text-xs">
                  <input type="checkbox" :checked="plannerForm.book_ids.includes(book.id)"
                    @change="togglePlannerBook(book.id)">
                  <span class="truncate">{{ book.title }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="mt-4 flex justify-end gap-2">
            <button class="acc-btn" @click="showPlannerModal = false">Скасувати</button>
            <button class="acc-btn-primary" :disabled="plannerSaving" @click="savePlanner">{{ plannerSaving ?
              'Створення...'
              : 'Створити план' }}</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="awardModal" class="fixed inset-0 z-[145]">
      <div class="acc-modal-overlay" @click="awardModal = null" />
      <div class="absolute inset-0 flex items-center justify-center p-4">
        <div class="acc-modal max-w-md">
          <div class="flex items-center gap-3">
            <img v-if="awardModal.image" :src="awardModal.image" :alt="awardModal.name"
              class="h-12 w-12 rounded-full object-cover border border-white/10" />
            <div v-else class="h-12 w-12 rounded-full flex items-center justify-center text-sm font-bold text-white"
              :style="{ backgroundColor: awardModal.color || '#8b5cf6' }">
              {{ (awardModal.name || '?').slice(0, 1).toUpperCase() }}
            </div>
            <div class="min-w-0">
              <div class="text-base font-extrabold truncate">{{ awardModal.name }}</div>
              <div v-if="awardModal.points" class="text-xs text-white/60">+{{ formatCount(awardModal.points, 'бал', 'бали', 'балів') }}</div>
            </div>
          </div>

          <div v-if="awardModal.description" class="mt-3 text-sm text-white/80">
            {{ awardModal.description }}
          </div>
          <div class="mt-3 space-y-1 text-xs text-white/65">
            <div v-if="awardModal.awarded_at_human">Отримано: {{ awardModal.awarded_at_human }}</div>
            <div v-if="awardModal.awarded_at">Дата: {{ awardModal.awarded_at }}</div>
          </div>

          <div class="mt-4 flex justify-end">
            <button class="acc-btn" type="button" @click="awardModal = null">Закрити</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import axios from 'axios';
import BookQuickPreviewHost from '../../components/books/BookQuickPreviewHost.vue';
import BookStatusBookmark from '../../components/books/BookStatusBookmark.vue';
import { canUseBookQuickPreview } from '../../utils/bookQuickPreviewCapability';
import { isVisibleBookStatus } from '../../utils/bookStatusPresentation';
import { formatBookCount, formatCount } from '../../utils/ukrainianPluralization';
import { coverPositionCssVars } from '../utils/profileAppearance';

export default {
  name: 'ProfileOverview',
  components: { BookQuickPreviewHost, BookStatusBookmark },
  props: {
    profile: { type: Object, default: null },
    dashboard: { type: Object, default: null },
    isOwner: { type: Boolean, default: false },
    isRestricted: { type: Boolean, default: false },
    viewer: { type: Object, default: null },
  },
  computed: {
        favoriteWorkCount() { return Number(this.dashboard?.favorite_work_count || 0); },
        favoriteWorkItems() { return (this.dashboard?.favorite_work_items || []).slice(0, 4); },
        showFavoriteWorks() { return !this.isRestricted && (this.isOwner || this.favoriteWorkCount > 0); },
        favoriteWorksUrl() { return this.profile?.username ? '/account/u/' + encodeURIComponent(this.profile.username) + '/favorites' : null; },
        favoriteWorkPreviewAvailable() { return this.showFavoriteWorks && canUseBookQuickPreview(Boolean(this.viewer)); },
        favoriteWorkPreviewItems() { return this.favoriteWorkItems.map(this.favoriteWorkPreviewItem).filter(Boolean); },
        favoriteWorkContextKey() { return JSON.stringify([this.profile?.username, this.viewer?.id, this.isRestricted, this.favoriteWorkItems.map((item) => item.work_id)]); },
    stats() {
      return this.dashboard?.stats || {};
    },
    heroCover() {
      return this.profile?.header?.image || '';
    },
    heroCoverCssVars() {
      return coverPositionCssVars(this.profile?.header?.position);
    },
    recentBooks() {
      return this.dashboard?.recent_read_books || [];
    },
    collections() {
      return this.dashboard?.collections || [];
    },
    librariesHubUrl() {
      if (this.isOwner) return '/libraries?mode=my';
      const username = this.profile?.username || '';
      return username ? `/libraries?user=${encodeURIComponent(username)}` : '/libraries';
    },
    awards() {
      return this.dashboard?.awards || [];
    },
    progressWidth() {
      const done = Number(this.stats.read_count || 0);
      const active = Number(this.stats.reading_count || 0);
      const planned = Number(this.stats.planned_count || 0);
      const plannerDone = Number(this.stats.planner_done_items || 0);
      const plannerTotal = Number(this.stats.planner_total_items || 0);
      const total = done + active + planned + plannerTotal;
      if (!total) return 6;
      return Math.min(100, Math.max(6, Math.round(((done + plannerDone) / total) * 100)));
    },
    readingPlans() {
      return this.dashboard?.reading_plans || [];
    },
    favoriteItems() {
      if (this.activeFavoritesTab === 'quotes') {
        return (this.dashboard?.favorite_quotes || []).map((item) => ({
          id: item.id,
          title: item.book_title || 'Цитата',
          meta: item.created_at_human || 'щойно',
          cover: item.book_cover || '',
        }));
      }

      // Default: saved reviews only (never library reading statuses)
      return (this.dashboard?.favorite_reviews || []).map((item) => ({
        id: item.id,
        title: item.book_title || 'Рецензія',
        meta: item.created_at_human || 'щойно',
        cover: item.book_cover || '',
      }));
    },
    activityBars() {
      const series = this.dashboard?.activity_series || [];
      const max = Number(this.stats.activity_max || 1);
      if (!series.length) {
        return Array.from({ length: 14 }).map(() => ({ value: 0, label: '', height: 8 }));
      }
      return series.map((point) => {
        const value = Number(point.value || 0);
        const height = Math.max(8, Math.round((value / max) * 40));
        return {
          value,
          label: point.label || '',
          height,
        };
      });
    },
    activitySummary() {
      const series = this.dashboard?.activity_series || [];
      if (!series.length) {
        return { total: 0, today: 0, avg: 0, peakLabel: '—' };
      }
      const values = series.map((p) => Number(p.value || 0));
      const total = values.reduce((acc, v) => acc + v, 0);
      const today = values[values.length - 1] || 0;
      const avg = (total / values.length).toFixed(1);
      let peakIndex = 0;
      for (let i = 1; i < values.length; i += 1) {
        if (values[i] > values[peakIndex]) peakIndex = i;
      }
      const peakValue = values[peakIndex] || 0;
      const peakLabel = peakValue > 0 ? `${series[peakIndex].label}: ${peakValue}` : '—';
      return { total, today, avg, peakLabel };
    },
    overviewMainValue() {
      if (this.overviewStatsTab === 'reviews') return Number(this.stats.reviews_count || 0);
      if (this.overviewStatsTab === 'discussions') return Number(this.stats.discussions_count || 0);
      if (this.overviewStatsTab === 'quotes') return Number(this.stats.quotes_count || 0);
      return Number(this.stats.read_count || 0);
    },
    overviewRows() {
      if (this.overviewStatsTab === 'reviews') {
        return [
          { key: 'reviews-total', label: 'Усього', value: Number(this.stats.reviews_count || 0) },
          { key: 'reviews-fav', label: 'Збережені', value: Number(this.stats.favorite_reviews_count || 0) },
          { key: 'reviews-rated', label: 'Оцінено книг', value: Number(this.stats.rated_count || 0) },
          { key: 'reviews-avg', label: 'Сер. оцінка', value: this.stats.average_rating ?? '—' },
        ];
      }
      if (this.overviewStatsTab === 'discussions') {
        return [
          { key: 'disc-total', label: 'Усього', value: Number(this.stats.discussions_count || 0) },
          { key: 'disc-drafts', label: 'Чернетки', value: Number((this.dashboard?.draft_discussions || []).length) },
          { key: 'disc-activity', label: 'Активність 14д', value: Number(this.activitySummary.total || 0) },
          { key: 'disc-today', label: 'Сьогодні', value: Number(this.activitySummary.today || 0) },
        ];
      }
      if (this.overviewStatsTab === 'quotes') {
        return [
          { key: 'quote-total', label: 'Усього', value: Number(this.stats.quotes_count || 0) },
          { key: 'quote-fav', label: 'Збережені', value: Number(this.stats.favorite_quotes_count || 0) },
          { key: 'quote-drafts', label: 'Чернетки', value: Number((this.dashboard?.draft_quotes || []).length) },
          { key: 'quote-today', label: 'Сьогодні', value: Number(this.activitySummary.today || 0) },
        ];
      }
      return [
        { key: 'book-reading', label: 'Читаю', value: Number(this.stats.reading_count || 0) },
        { key: 'book-planned', label: 'Заплановано', value: Number(this.stats.planned_count || 0) },
        { key: 'book-dropped', label: 'Закинуто', value: Number(this.stats.dropped_count || 0) },
        { key: 'book-total', label: 'Усього книг', value: Number(this.stats.total_books_count || 0) },
      ];
    },
    overviewRingColor() {
      if (this.overviewStatsTab === 'reviews') return '#f59e0b';
      if (this.overviewStatsTab === 'discussions') return '#06b6d4';
      if (this.overviewStatsTab === 'quotes') return '#10b981';
      return '#8b5cf6';
    },
    overviewRingPercent() {
      if (this.overviewStatsTab === 'reviews') {
        const total = Number(this.stats.reviews_count || 0);
        const fav = Number(this.stats.favorite_reviews_count || 0);
        if (!total) return 0;
        return Math.min(100, Math.round((fav / total) * 100));
      }
      if (this.overviewStatsTab === 'discussions') {
        const total = Number(this.stats.discussions_count || 0);
        const today = Number(this.activitySummary.today || 0);
        if (!total) return 0;
        return Math.min(100, Math.round((today / total) * 100));
      }
      if (this.overviewStatsTab === 'quotes') {
        const total = Number(this.stats.quotes_count || 0);
        const fav = Number(this.stats.favorite_quotes_count || 0);
        if (!total) return 0;
        return Math.min(100, Math.round((fav / total) * 100));
      }
      const totalBooks = Number(this.stats.total_books_count || 0);
      const read = Number(this.stats.read_count || 0);
      if (!totalBooks) return 0;
      return Math.min(100, Math.round((read / totalBooks) * 100));
    },
    overviewRingDasharray() {
      const circumference = 2 * Math.PI * 44;
      const progress = (this.overviewRingPercent / 100) * circumference;
      return `${progress} ${circumference}`;
    },
  },
    watch: {
        favoriteWorkContextKey: { immediate: true, handler() {
            this.$refs.favoriteWorkQuickPreview?.close({ restoreFocus: false });
            this.favoriteWorkViewerContexts = {};
            this.favoriteWorkBookFacts = {};
            this.favoriteWorkContextState = 'idle';
            return this.refreshFavoriteWorkViewerContexts();
        } },
    },
    beforeDestroy() {
        this.favoriteWorkContextGeneration += 1;
        this.$refs.favoriteWorkQuickPreview?.close({ restoreFocus: false });
    },
  data() {
    return {
            favoriteWorkViewerContexts: {}, favoriteWorkBookFacts: {}, favoriteWorkContextState: 'idle',
            favoriteWorkContextGeneration: 0, favoriteWorkPreviewOpenId: null,
            favoriteWorkPreviewId: 'book-quick-preview-owner-library',
      showPlannerModal: false,
      overviewStatsTab: 'books',
      activeFavoritesTab: 'reviews',
      plannerBookQuery: '',
      plannerSearchResults: [],
      plannerSaving: false,
      awardModal: null,
      plannerForm: {
        title: '',
        goal: '',
        target_date: '',
        book_ids: [],
      },
    };
  },
  methods: {
        async refreshFavoriteWorkViewerContexts() {
            const generation = ++this.favoriteWorkContextGeneration;
            const key = this.favoriteWorkContextKey;
            const viewerId = this.viewer?.id;
            const ids = this.favoriteWorkItems.map((item) => item.work_id);
            if (!viewerId || !ids.length || !this.showFavoriteWorks) {
                this.favoriteWorkViewerContexts = {};
                this.favoriteWorkBookFacts = {};
                this.favoriteWorkContextState = 'loaded';
                return;
            }
            // Keep the same-viewer snapshot until an action refresh can atomically rebind the canonical host.
            if (this.favoriteWorkContextState !== 'loaded') this.favoriteWorkContextState = 'loading';
            const current = () => generation === this.favoriteWorkContextGeneration && key === this.favoriteWorkContextKey;
            try {
                const { data } = await axios.get('/api/account/book-preview-contexts', { params: { book_ids: ids } });
                if (!current()) return;
                if (Number(data?.viewer_id) !== Number(viewerId) || !data.viewer_contexts ||
                    ids.some((id) => !Object.prototype.hasOwnProperty.call(data.viewer_contexts, id))) throw new Error('Invalid viewer snapshot');
                this.favoriteWorkViewerContexts = data.viewer_contexts;
                this.favoriteWorkBookFacts = data.book_facts || {};
                this.favoriteWorkContextState = 'loaded';
            } catch {
                if (!current()) return;
                this.favoriteWorkViewerContexts = {};
                this.favoriteWorkBookFacts = {};
                this.favoriteWorkContextState = 'error';
                this.$refs.favoriteWorkQuickPreview?.close({ restoreFocus: false });
            }
        },
        favoriteWorkViewerStatus(item) {
            const status = this.favoriteWorkViewerContexts[item.work_id]?.shelf_status;
            return this.viewer && this.favoriteWorkContextState === 'loaded' && isVisibleBookStatus(status) ? status : null;
        },
        favoriteWorkPreviewItem(item) {
            const context = this.favoriteWorkViewerContexts[item.work_id];
            if (!this.favoriteWorkPreviewAvailable || this.favoriteWorkContextState !== 'loaded' || !context) return null;
            return {
                id: item.work_id,
                book: { id: item.work_id, slug: item.slug, title: item.title, cover: item.cover, ...this.favoriteWorkBookFacts[item.work_id] },
                status: context.shelf_status, card_context: context,
            };
        },
        openFavoriteWorkPreview(item, event, immediate = false) {
            const preview = this.favoriteWorkPreviewItem(item);
            if (preview) this.$refs.favoriteWorkQuickPreview?.scheduleOpen(preview, event.currentTarget, immediate);
        },
        closeFavoriteWorkPreview() { this.$refs.favoriteWorkQuickPreview?.scheduleClose(); },
        favoriteWorkPreviewKeydown(item, event) {
            const preview = this.favoriteWorkPreviewItem(item);
            if (preview) this.$refs.favoriteWorkQuickPreview?.handleTriggerKeydown(event, preview, event.currentTarget);
        },
        openFavoriteWorkViewerManagement() {
            const url = this.$refs.favoriteWorkQuickPreview?.activeContext?.managementUrl;
            if (url) window.location.assign(url);
        },
    formatBookCount,
    formatCount,
    resolveBookCover(cover) {
      return cover || '/images/placeholders/book-cover.svg';
    },
    onCoverError(event) {
      const fallback = '/images/placeholders/book-cover.svg';
      if (event?.target && !String(event.target.src || '').includes(fallback)) {
        event.target.src = fallback;
      }
    },
    async openPlannerModal() {
      this.showPlannerModal = true;
      this.plannerForm = { title: '', goal: '', target_date: '', book_ids: [] };
      this.plannerBookQuery = '';
      this.plannerSearchResults = [];
    },
    async searchPlannerBooks() {
      if (!this.plannerBookQuery.trim()) {
        this.plannerSearchResults = [];
        return;
      }
      const { data } = await axios.get('/api/account/books/search', { params: { q: this.plannerBookQuery.trim() } });
      this.plannerSearchResults = data?.books || [];
    },
    togglePlannerBook(bookId) {
      if (this.plannerForm.book_ids.includes(bookId)) {
        this.plannerForm.book_ids = this.plannerForm.book_ids.filter((id) => id !== bookId);
        return;
      }
      this.plannerForm.book_ids.push(bookId);
    },
    async savePlanner() {
      if (!this.plannerForm.title.trim()) {
        alert('Вкажіть назву плану читання.');
        return;
      }
      this.plannerSaving = true;
      try {
        await axios.post('/api/account/reading-plans', this.plannerForm);
        window.location.reload();
      } catch (e) {
        alert(e?.response?.data?.message || 'Не вдалося створити план читання.');
      } finally {
        this.plannerSaving = false;
      }
    },
    async togglePlanItem(plan, item) {
      try {
        await axios.put(`/api/account/reading-plans/${plan.id}/items/${item.id}`, {
          is_done: !item.is_done,
        });
        item.is_done = !item.is_done;
      } catch (e) {
        alert('Не вдалося оновити пункт плану.');
      }
    },
    openAwardModal(award) {
      this.awardModal = award || null;
    },
  },
};
</script>

<style scoped>
/* Match the existing Library keyboard-only Preview affordance. */
.favorite-work-preview-trigger {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0 !important;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}
.favorite-work-preview-trigger:focus-visible {
    position: static;
    width: auto;
    height: 2.5rem;
    margin: 0;
    padding: 0.5rem 0.75rem !important;
    overflow: visible;
    clip: auto;
    white-space: normal;
    border-width: 1px;
}
</style>
